export interface PageViewProperties {
  path: string;
  referrer: string;
  search: string;
  title: string;
  url: string;
  name: string;
  category: string;
  language: string;
  page_type: string;
  kiosk: boolean;
}

export interface ProductAddedProperties {
  cart_id: string;
  name: string;
  price: number;
  product_id: string | number;
  quantity: number;
  site: string | null;
  sku: string;
  brand?: string;
  builder?: string;
  category?: string;
  list_id?: string;
  max_price?: string;
  min_price?: string;
  position?: number;
  url?: string;
  variant?: number;
}

/** Analytics-only constants (not product catalog values). */
export const GOLD_MEMBERSHIP_PRODUCT_ADDED = {
  name: "Print Connect Gold Membership",
  list_id: "registration form__become a member__na--na__add to cart",
  category: "Membership",
} as const;

/**
* Tells TypeScript that Segment may attach analytics on window, with a page() method.
*/
declare global {
  interface Window {
    analytics?: {
      page: (
        properties?: PageViewProperties
      ) => Promise<void> | void;
      track: (eventName: string, properties?: Record<string, unknown>) => void;
      initialized?: boolean;
    };
  }
}

const firePage = (properties: PageViewProperties) => {
  if (typeof window === "undefined" || !window.analytics) return false;
  try {
    void window.analytics.page(properties);
    return true;
  } catch (error) {
    console.log("unable to track page event");
    return false;
  }
};

/**
 * Fire a Segment page call when the user views a page.
 * Retries briefly while the Segment snippet finishes loading from the CDN.
 */
export const pageViewEvent = (properties: PageViewProperties) => {
  if (firePage(properties)) return;

  let attempts = 0;
  const maxAttempts = 40; 
  const intervalId = window.setInterval(() => {
    attempts += 1;
    if (firePage(properties) || attempts >= maxAttempts) {
      window.clearInterval(intervalId);
    }
  }, 250);
};

const fireTrack = (
  eventName: string,
  properties?: Record<string, unknown> | ProductAddedProperties,
) => {
  if (typeof window === 'undefined' || !window.analytics || typeof window.analytics.track !== 'function') {
    return false;
  }
  try {
    window.analytics.track(eventName, properties as Record<string, unknown>);
    return true;
  } catch (error) {
    console.error('Unable to fire track event', error);
    return false;
  }
};

export const trackErrorViewed = (errorMessage: string, errorType = 'Print Connect') => {
  const properties = { error_message: errorMessage, error_type: errorType };
  if (fireTrack('Error Viewed', properties)) return;
  let attempts = 0;
  const maxAttempts = 40;
  const intervalId = window.setInterval(() => {
    attempts += 1;
    if (fireTrack('Error Viewed', properties) || attempts >= maxAttempts) {
      window.clearInterval(intervalId);
    }
  }, 250);
};

export const trackProductAdded = (properties: ProductAddedProperties) => {
  if (fireTrack("Product Added", properties)) return;
  let attempts = 0;
  const maxAttempts = 40;
  const intervalId = window.setInterval(() => {
    attempts += 1;
    if (fireTrack("Product Added", properties) || attempts >= maxAttempts) {
      window.clearInterval(intervalId);
    }
  }, 250);
};

export const trackGoldMembershipProductAdded = (params: {
  cartId: string;
  sku: string;
  productId: string | number;
  price: number;
  quantity: number;
  name?: string;
}) => {
  trackProductAdded({
    cart_id: params.cartId,
    name: params.name || GOLD_MEMBERSHIP_PRODUCT_ADDED.name,
    price: params.price,
    product_id: params.productId,
    quantity: params.quantity,
    site: null,
    sku: params.sku,
    category: GOLD_MEMBERSHIP_PRODUCT_ADDED.category,
    list_id: GOLD_MEMBERSHIP_PRODUCT_ADDED.list_id,
  });
};

export const loadSegmentSnippet = (apiKey: string) => {
  if (typeof window === "undefined" || !apiKey) return;
  if (window.analytics || document.getElementById("segment-snippet")) return;

  // Standard Segment analytics.js loader (auto page views disabled)
  const script = document.createElement("script");
  script.id = "segment-snippet";
  script.type = "text/javascript";
  script.text = `
    !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){if(window.analytics.initialized)return window.analytics[e].apply(window.analytics,arguments);var i=Array.prototype.slice.call(arguments);i.unshift(e);analytics.push(i);return analytics}};for(var i=0;i<analytics.methods.length;i++){var key=analytics.methods[i];analytics[key]=analytics.factory(key)}analytics.load=function(key,i){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=i};analytics._writeKey="${apiKey}";analytics.SNIPPET_VERSION="4.15.3";analytics.load("${apiKey}");}}();
  `;
  document.head.appendChild(script);
};
